import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Accordion, AccordionDetails, AccordionSummary, Box, LinearProgress, Paper, Typography } from '@mui/material';
import { CheckCircleOutline, ErrorOutline, EventAvailableOutlined, ExpandMore, Inventory2Outlined, WarningAmber } from '@mui/icons-material';
import { Button, FinanceSummaryCard, LightChip } from '@uplift/ui-v2';
import { CalendarDateService } from '@uplift/services';
import { ROUTING_CONFIG, paymentsTabRoute } from '@uplift/constants';
import { mobileBreakpoint, useIsMobile, useL10n } from '@uplift/hooks';
import { CoveredSession, PatientBetterhelpReferralCoverage } from '@uplift/api';
import { useUserInsurancesQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';

const COVERAGE_DATE_TZ = 'America/New_York';

const referralTypeLabel = (l10n: ReturnType<typeof useL10n>, referralType: string): string => {
  const labels: Record<string, string> = l10n.payments.coverageTab.referralType;

  return labels[referralType] ?? referralType;
};

const CoveredSessionHistory: React.VFC<{ referral: PatientBetterhelpReferralCoverage }> = ({ referral }) => {
  const isMobile = useIsMobile(mobileBreakpoint);
  const l10n = useL10n();
  const copy = l10n.payments.coverageTab;
  const sessions = referral.coveredSessions;

  return (
    <Accordion
      elevation={0}
      square
      disableGutters
      defaultExpanded
      slotProps={{ transition: { unmountOnExit: true } }}
      sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'transparent', '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore fontSize="small" sx={{ color: 'text.secondary' }} />}
        sx={theme => ({
          p: 0,
          minHeight: 'unset',
          '&.Mui-expanded': { minHeight: 'unset' },
          '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': { margin: theme.spacing(1.5, 0, 0.5) },
        })}
      >
        <Typography variant="body2" color="textSecondary" fontWeight={600}>
          {copy.coveredSessionHistoryTitle}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {sessions.length === 0 && (
          <Box py={1} sx={{ '&:not(:last-of-type)': { borderBottom: '1px dashed', borderColor: 'divider' } }}>
            <Typography variant="body2" color="text.primary" fontWeight={600}>{copy.emptySessionHistoryTitle}</Typography>
            <Typography variant="body2" color="textSecondary">
              {copy.emptySessionHistorySubtitle}
            </Typography>
          </Box>
        )}
        {sessions.map((session: CoveredSession, index: number) => (
          <Box
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            justifyContent="space-between"
            alignItems="flex-start"
            gap={isMobile ? 0.5 : 0}
            py={1}
            key={`${session.sessionDate}-${index}`}
            sx={{ '&:not(:last-of-type)': { borderBottom: '1px dashed', borderColor: 'divider' } }}
          >
            <Box>
              <Typography variant="body2" color="text.primary" fontWeight={600}>
                {copy.sessionRowTitle(referral.sessionsConsumed - index, referral.sessionCount, session.providerName)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {copy.sessionRowSubtitle(referralTypeLabel(l10n, session.referralType), session.eapName)}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              {CalendarDateService.date(session.sessionDate).tz(COVERAGE_DATE_TZ).format('MMM D, YYYY')}
            </Typography>
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

const CoverageChanges: React.VFC<{ referral: PatientBetterhelpReferralCoverage }> = ({ referral }) => {
  const isMobile = useIsMobile(mobileBreakpoint);
  const l10n = useL10n();
  const copy = l10n.payments.coverageTab;
  const grants = [ ...referral.grants ].sort((a, b) => b.grantDate.localeCompare(a.grantDate));

  return (
    <Accordion
      elevation={0}
      square
      disableGutters
      slotProps={{ transition: { unmountOnExit: true } }}
      sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'transparent', '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore fontSize="small" sx={{ color: 'text.secondary' }} />}
        sx={theme => ({
          p: 0,
          minHeight: 'unset',
          '&.Mui-expanded': { minHeight: 'unset' },
          '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': { margin: theme.spacing(1.5, 0, 0.5) },
        })}
      >
        <Typography variant="body2" color="textSecondary" fontWeight={600}>
          {copy.coverageChangesTitle}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {grants.map(grant => (
          <Box
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            justifyContent="space-between"
            alignItems="flex-start"
            gap={isMobile ? 0.5 : 0}
            py={1}
            key={grant.referralUuid}
            sx={{ '&:not(:last-of-type)': { borderBottom: '1px dashed', borderColor: 'divider' } }}
          >
            <Box>
              <Typography variant="body2" color="text.primary" fontWeight={600}>
                {copy.grantRowTitle(grant.sessionCount)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {copy.grantRowSubtitle(referralTypeLabel(l10n, grant.referralType), grant.eapName)}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              {CalendarDateService.date(grant.grantDate).tz(COVERAGE_DATE_TZ).format('MMM D, YYYY')}
            </Typography>
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export const CoverageTab: React.VFC<{ referral: PatientBetterhelpReferralCoverage }> = ({ referral }) => {
  const history = useHistory();
  const isMobile = useIsMobile(mobileBreakpoint);
  const l10n = useL10n();
  const copy = l10n.payments.coverageTab;
  const me = useCurrentuser();
  const { data: insurances } = useUserInsurancesQuery(me.id);
  const hasInsurance = !!insurances && insurances.length > 0;
  const progress = referral.sessionCount > 0
    ? (referral.sessionsRemaining / referral.sessionCount) * 100
    : 0;
  const isExhausted = referral.sessionsRemaining === 0;
  const isLow = !isExhausted && (referral.sessionsRemaining === 1 || progress <= 33);

  let progressColor: 'error' | 'warning' | 'primary' = 'primary';

  if (isExhausted) progressColor = 'error';
  else if (isLow) progressColor = 'warning';

  const coveringCompany = referral.grants.length > 0
    ? [ ...referral.grants ].sort((a, b) => b.grantDate.localeCompare(a.grantDate))[0].eapName
    : referralTypeLabel(l10n, referral.referralType);

  const insetPx = isMobile ? 2 : 0;

  return (
    <Box display="flex" flexDirection="column" gap={3} width="100%" boxSizing="border-box">
      <Typography variant="h4" sx={{ px: insetPx }}>{copy.heading}</Typography>
      <Paper
        elevation={isMobile ? 0 : 4}
        square={isMobile}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          width: '100%',
          boxSizing: 'border-box',
          px: isMobile ? 2 : 5,
          py: isMobile ? 3 : 5,
        }}
      >
        <Box>
          <Typography variant="body1" fontWeight={600}>{coveringCompany}</Typography>
          <Box display="flex" gap={1} mt={1}>
            <LightChip color="betterhelp" label={referralTypeLabel(l10n, referral.referralType)} size="small" />
            <LightChip color={referral.isActive ? 'success' : 'info'} label={referral.isActive ? copy.status.active : copy.status.inactive} size="small" />
          </Box>
        </Box>

        <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} width="100%" gap={2}>
          <FinanceSummaryCard
            value={referral.sessionsRemaining}
            label={copy.metrics.sessionsRemaining}
            icon={<EventAvailableOutlined />}
            color="greenDark"
            sx={{ width: '100%' }}
          />
          <FinanceSummaryCard
            value={referral.sessionsConsumed}
            label={copy.metrics.sessionsUsed}
            icon={<CheckCircleOutline />}
            color="green"
            sx={{ width: '100%' }}
          />
          <FinanceSummaryCard
            value={referral.sessionCount}
            label={copy.metrics.sessionsCovered}
            icon={<Inventory2Outlined />}
            color="greenLight"
            sx={{ width: '100%' }}
          />
        </Box>

        <Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            color={progressColor}
            sx={{
              borderRadius: 3,
              height: 6,
              bgcolor: 'grey.100',
              mt: 1,
              '& .MuiLinearProgress-bar': { borderRadius: 3 },
            }}
          />
        </Box>

        {isExhausted && (
          <Box display="flex" alignItems="center" gap={1} bgcolor="error.background" borderRadius={1} px={2} py={1.5} boxSizing="border-box">
            <ErrorOutline fontSize="small" sx={{ color: 'error.main' }} />
            <Typography variant="body2" color="error.dark" fontWeight={500}>
              {hasInsurance ? copy.exhaustedInsuranceMessage : copy.exhaustedCashPayMessage}
            </Typography>
          </Box>
        )}

        {isLow && (
          <Box display="flex" alignItems="center" gap={1} bgcolor="warning.background" borderRadius={1} px={2} py={1.5} boxSizing="border-box">
            <WarningAmber fontSize="small" sx={{ color: 'warning.main' }} />
            <Typography variant="body2" color="warning.dark" fontWeight={500}>
              {hasInsurance ? copy.lowInsuranceMessage(referral.sessionsRemaining) : copy.lowCashPayMessage(referral.sessionsRemaining)}
            </Typography>
          </Box>
        )}

        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'flex-start' : 'center'}
          justifyContent="space-between"
          gap={2}
          p={2}
          border="1px solid"
          borderColor="divider"
          borderRadius={1}
          boxSizing="border-box"
        >
          <Box>
            <Typography variant="body2" color="textSecondary">{copy.afterCoverage.label}</Typography>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              {hasInsurance ? copy.afterCoverage.billedToInsurance : copy.afterCoverage.billedAtSelfPay}
            </Typography>
          </Box>
          {!hasInsurance && (
            <Button variant="contained" color="secondary" size="small" fullWidth={isMobile} component={Link} to={ROUTING_CONFIG.insurance}>
              {copy.addInsurance}
            </Button>
          )}
        </Box>

        <CoveredSessionHistory referral={referral} />
        {referral.grants.length > 0 && <CoverageChanges referral={referral} />}
      </Paper>

      <Box display="flex" justifyContent={isMobile ? 'stretch' : 'flex-end'} px={insetPx}>
        <Button
          variant="outlined"
          color="secondary"
          fullWidth={isMobile}
          onClick={() => history.push(paymentsTabRoute.createLink({ tab: 'overview' }))}
        >
          {copy.viewPaymentHistory}
        </Button>
      </Box>
    </Box>
  );
};
